import type { MouseEvent, PropsWithChildren, ReactNode } from 'react';

interface AnchorProps extends PropsWithChildren {
	href: string;

	title?: string;
}

/**
 *	An in-page link — the `[jump](#section)` form. Scrolls to the element the
 *	fragment names instead of navigating anywhere.
 *
 *	It cannot stay a plain anchor. `index.html` carries `<base href="/">`, which
 *	relative URLs in the built bundle depend on, and a bare `#section` resolves
 *	against *that* rather than against the document — so the browser leaves the
 *	page and reloads the app at `/#section`.
 *
 *	The default is prevented whether or not the target exists: a fragment naming
 *	a heading that has since been renamed should do nothing, rather than throw
 *	the reader back to the landing page.
 */
export function Anchor({ href, children, ...props }: AnchorProps): ReactNode {
	function scrollIntoView(event: MouseEvent<HTMLAnchorElement>): void {
		event.preventDefault();

		document.getElementById(toElementId(href))?.scrollIntoView();
	}

	return (
		<a href={href} onClick={scrollIntoView} {...props}>
			{children}
		</a>
	);
}

/**
 *	The id a fragment names. markdown-it percent-encodes an href, so a heading
 *	with a non-ASCII word arrives escaped while its `id` attribute does not.
 */
function toElementId(href: string): string {
	const fragment = href.slice(1);

	try {
		return decodeURIComponent(fragment);
	} catch {
		return fragment;
	}
}
